import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowRight,
  FiFileText,
  FiCalendar,
  FiActivity,
  FiUser,
  FiSend,
  FiCheck,
  FiHelpCircle,
} from 'react-icons/fi';

import { API_BASE_URL as API_ROOT } from '../lib/session';
// Same production-API pattern used by Auth.js's API_BASE_URL — literal
// Render URL, not localhost/relative, so the contact form works against
// the real deployed backend once server/routes/contact.js is live.
const API_BASE_URL = `${API_ROOT}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NAV_LINKS = [
  { label: 'À propos', key: 'apropos' },
  { label: 'FAQ', key: 'faq' },
  { label: 'Contact', key: 'contact' },
];

const FAQS = [
  {
    q: 'Qui peut accéder à la plateforme ?',
    a: "L'accès est réservé au personnel autorisé du service — médecins, secrétaires, infirmier(e)s, gestionnaires et autres rôles habilités — chacun avec un espace adapté à sa fonction.",
  },
  {
    q: 'Comment obtenir un compte ?',
    a: "Les comptes sont créés par l'administration du service. Rapprochez-vous de votre gestionnaire ou de l'administrateur de la plateforme.",
  },
  {
    q: 'Mot de passe oublié ?',
    a: "Contactez l'administrateur de la plateforme pour réinitialiser vos identifiants.",
  },
  {
    q: 'Mes données sont-elles protégées ?',
    a: "L'accès est authentifié et cloisonné par rôle : chaque utilisateur ne voit que les informations utiles à sa fonction.",
  },
];

// Presentational only — restates the hero description as scannable tags,
// not new claims.
const SCOPE_TAGS = [
  { label: 'Dossiers patients', icon: FiFileText },
  { label: 'Programme opératoire', icon: FiCalendar },
  { label: 'Suivi post-opératoire', icon: FiActivity },
];

// Contact's dedicated visual identity: a real, working form (not a
// decorative panel). Lives as its own component to keep its form state
// (values/errors/submit status) isolated from the rest of the page.
const ContactForm = () => {
  const [values, setValues] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [serverError, setServerError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = 'Votre nom est requis.';
    if (!values.email.trim()) next.email = 'Votre email est requis.';
    else if (!EMAIL_RE.test(values.email.trim())) next.email = "Format d'email invalide.";
    if (!values.message.trim()) next.message = 'Un message est requis.';
    else if (values.message.trim().length < 10) next.message = 'Votre message est un peu court.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'loading') return;
    if (!validate()) return;

    setStatus('loading');
    setServerError('');

    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      if (!response.ok) throw new Error(`Erreur ${response.status}`);

      setStatus('success');
    } catch (error) {
      setStatus('error');
      setServerError(
        "L'envoi a échoué. Le service est peut-être temporairement indisponible — réessayez dans un instant ou contactez directement l'administrateur de la plateforme."
      );
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center rounded-2xl bg-mist px-6 py-10 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-600 text-white">
          <FiCheck size={22} />
        </span>
        <p className="mt-4 text-[16px] font-semibold text-ink">Message envoyé</p>
        <p className="mt-1.5 max-w-xs text-[14px] leading-relaxed text-black/55">
          Merci, votre message a bien été transmis. L&apos;administrateur de la plateforme reviendra vers vous.
        </p>
        <button
          type="button"
          onClick={() => {
            setValues({ name: '', email: '', message: '' });
            setStatus('idle');
          }}
          className="mt-5 rounded text-[13px] font-medium text-brand-600 underline underline-offset-4 transition hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
        >
          Envoyer un autre message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="mt-5 space-y-4">
      <div>
        <label htmlFor="contact-name" className="mb-1.5 block text-[13px] font-medium text-black/60">
          Nom
        </label>
        <input
          id="contact-name"
          name="name"
          type="text"
          autoComplete="name"
          disabled={status === 'loading'}
          value={values.name}
          onChange={handleChange}
          placeholder="Votre nom"
          aria-invalid={!!errors.name}
          className={`w-full rounded-xl border bg-white px-4 py-3 text-[15px] text-ink outline-none transition placeholder:text-black/30 focus:ring-4 focus:ring-brand-600/10 disabled:opacity-60 ${
            errors.name ? 'border-red-400 focus:border-red-400' : 'border-black/10 focus:border-brand-600'
          }`}
        />
        {errors.name && <p className="mt-1.5 text-[12.5px] text-red-500">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="contact-email" className="mb-1.5 block text-[13px] font-medium text-black/60">
          Email
        </label>
        <input
          id="contact-email"
          name="email"
          type="email"
          autoComplete="email"
          disabled={status === 'loading'}
          value={values.email}
          onChange={handleChange}
          placeholder="vous@exemple.com"
          aria-invalid={!!errors.email}
          className={`w-full rounded-xl border bg-white px-4 py-3 text-[15px] text-ink outline-none transition placeholder:text-black/30 focus:ring-4 focus:ring-brand-600/10 disabled:opacity-60 ${
            errors.email ? 'border-red-400 focus:border-red-400' : 'border-black/10 focus:border-brand-600'
          }`}
        />
        {errors.email && <p className="mt-1.5 text-[12.5px] text-red-500">{errors.email}</p>}
      </div>

      <div>
        <label htmlFor="contact-message" className="mb-1.5 block text-[13px] font-medium text-black/60">
          Message
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={4}
          disabled={status === 'loading'}
          value={values.message}
          onChange={handleChange}
          placeholder="Votre question ou votre demande..."
          aria-invalid={!!errors.message}
          className={`w-full resize-none rounded-xl border bg-white px-4 py-3 text-[15px] leading-relaxed text-ink outline-none transition placeholder:text-black/30 focus:ring-4 focus:ring-brand-600/10 disabled:opacity-60 ${
            errors.message ? 'border-red-400 focus:border-red-400' : 'border-black/10 focus:border-brand-600'
          }`}
        />
        {errors.message && <p className="mt-1.5 text-[12.5px] text-red-500">{errors.message}</p>}
      </div>

      {status === 'error' && (
        <p role="alert" className="rounded-xl bg-red-50 px-3.5 py-2.5 text-[13px] leading-relaxed text-red-600">
          {serverError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-600 py-3.5 text-[15px] font-semibold text-white transition hover:bg-brand-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === 'loading' ? (
          <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            Envoi en cours...
          </>
        ) : (
          <>
            Envoyer le message
            <FiSend size={15} />
          </>
        )}
      </button>
    </form>
  );
};

const Home = () => {
  const navigate = useNavigate();

  // FAQ's vertical-timeline reveal is direction-aware, not a one-shot: the
  // observer stays attached for the page's lifetime and toggles faqInView
  // both ways, so scrolling the section out of view resets it to hidden
  // and scrolling back in replays the reveal.
  const faqSectionRef = useRef(null);
  const [faqInView, setFaqInView] = useState(false);

  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const node = faqSectionRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(([entry]) => setFaqInView(entry.isIntersecting), {
      threshold: 0.25,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans text-ink">
      <style>{`
        html {
          scroll-behavior: smooth;
        }
        @keyframes home-fade-up {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes home-logo-in {
          0% { opacity: 0; transform: scale(0.55) rotate(-10deg); }
          55% { opacity: 1; transform: scale(1.06) rotate(3deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        @keyframes home-logo-bloom {
          0% { opacity: 0; transform: scale(0.5); }
          35% { opacity: 0.55; }
          100% { opacity: 0; transform: scale(1.9); }
        }
        .home-reveal { animation: home-fade-up 0.7s cubic-bezier(0.16,1,0.3,1) both; }
        .home-logo-reveal { animation: home-logo-in 0.9s cubic-bezier(0.16,1,0.3,1) both; }
        .home-logo-bloom { animation: home-logo-bloom 1.15s cubic-bezier(0.16,1,0.3,1) both; }
        @media (prefers-reduced-motion: reduce) {
          html { scroll-behavior: auto; }
          .home-reveal, .home-logo-reveal, .home-logo-bloom {
            animation: none;
          }
        }
      `}</style>

      {/* Nav */}
      <header className="relative z-20">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8 sm:py-6">
          <span className="text-[13px] font-semibold tracking-tight text-ink sm:text-[15px]">
            CTCV-CUOMO
            <span className="hidden font-normal text-black/40 sm:inline"> · Hôpital Fann</span>
          </span>

          <div className="flex items-center gap-1">
            <nav className="hidden items-center gap-0.5 sm:flex" aria-label="Informations">
              {NAV_LINKS.map(({ label, key }) => (
                <a
                  key={key}
                  href={`#${key}`}
                  className="rounded-full px-3.5 py-2 text-[13px] font-medium text-black/55 transition hover:bg-black/5 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 active:bg-black/10"
                >
                  {label}
                </a>
              ))}
            </nav>

            <button
              type="button"
              onClick={() => navigate('/auth')}
              className="ml-1 flex items-center gap-1.5 rounded-full bg-brand-600 py-2 pl-3.5 pr-1.5 text-[12px] font-semibold text-white transition hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 active:bg-brand-800 sm:gap-2 sm:py-2 sm:pl-4 sm:pr-2 sm:text-[13px]"
            >
              Se connecter
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20 sm:h-6 sm:w-6">
                <FiArrowRight size={12} />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero — a real two-column split: the logo alone on the left keeping
          its own distinctive entrance, everything else (eyebrow, headline,
          description, scope tags, CTAs) grouped as one left-aligned column
          on the right, roughly where the old decorative cross motif used to
          sit (dropped — it would now collide with real text). Collapses to
          a single column (logo above text) below the lg breakpoint. */}
      <main className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle,rgba(0,113,227,0.22)_1px,transparent_1.6px)] bg-[length:30px_30px]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/20 via-white/70 to-white"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-[-10%] top-[-16%] h-[420px] w-[420px] rounded-full bg-brand-100 opacity-70 blur-[100px] sm:h-[560px] sm:w-[560px]"
        />

        <div className="relative mx-auto max-w-7xl px-6 pb-20 pt-10 sm:px-8 sm:pb-28 sm:pt-14 lg:pt-20">
          <div className="flex flex-col items-start gap-10 lg:flex-row lg:items-center lg:gap-16">
            {/* Left column — logo only, its own distinctive entrance: a
                scale + rotate-in reveal with a soft glow bloom behind it,
                deliberately different from the fade-up used by the text
                column. */}
            <div className="home-logo-reveal relative shrink-0">
              <div aria-hidden="true" className="home-logo-bloom absolute inset-0 -z-10 rounded-full bg-brand-300" />
              <div aria-hidden="true" className="absolute inset-0 -z-10 scale-125 rounded-full bg-white blur-2xl" />
              <img
                src="/logo.png"
                alt="Logo de la plateforme du service CTCV-CUOMO, Centre Hospitalier National Fann"
                className="h-28 w-28 rounded-[28px] bg-white object-contain p-[12px] sm:p-[16px] lg:p-[24px] shadow-[0_28px_60px_-18px_rgba(0,55,142,0.45)] ring-1 ring-black/5 sm:h-40 sm:w-40 sm:rounded-[36px] lg:h-56 lg:w-56 lg:rounded-[44px]"
              />
            </div>

            {/* Right column — everything that isn't the logo, as one
                left-aligned group, same full type scale as before. */}
            <div className="flex max-w-xl flex-col items-start text-left lg:max-w-2xl">
              <span className="home-reveal inline-flex items-center rounded-full bg-brand-50 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-700 sm:text-[12px]">
                Plateforme interne · Personnel autorisé
              </span>

              <h1
                className="home-reveal mt-6 text-[2.5rem] font-semibold leading-[1.04] tracking-tight text-ink sm:mt-7 sm:text-6xl lg:text-7xl"
                style={{ animationDelay: '0.14s' }}
              >
                L’intelligence des données au service des soignants.
              </h1>

              <p
                className="home-reveal mt-6 max-w-lg text-[15.5px] leading-relaxed text-black/55 sm:text-[17px]"
                style={{ animationDelay: '0.26s' }}
              >
                CTCV-CUOMO réunit dossiers patients, programme opératoire et suivi post-opératoire
                dans un espace unique, pensé pour le personnel du service.
              </p>

              <div className="home-reveal mt-7 flex flex-wrap items-center gap-2" style={{ animationDelay: '0.34s' }}>
                {SCOPE_TAGS.map(({ label, icon: Icon }) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1.5 rounded-full bg-mist px-3.5 py-1.5 text-[12.5px] font-medium text-black/60 sm:text-[13px]"
                  >
                    <Icon size={13} className="text-brand-600" aria-hidden="true" />
                    {label}
                  </span>
                ))}
              </div>

              <div
                className="home-reveal mt-10 flex flex-col items-stretch gap-4 sm:flex-row sm:items-center"
                style={{ animationDelay: '0.42s' }}
              >
                <button
                  type="button"
                  onClick={() => navigate('/auth')}
                  className="group flex w-full items-center justify-center gap-2.5 rounded-full bg-brand-600 px-7 py-3.5 text-[15px] font-semibold text-white shadow-[0_18px_34px_-14px_rgba(0,113,227,0.6)] transition hover:bg-brand-700 hover:shadow-[0_22px_40px_-14px_rgba(0,113,227,0.65)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 active:scale-[0.98] active:bg-brand-800 sm:w-auto"
                >
                  Se connecter
                  <FiArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                </button>

                <a
                  href="#apropos"
                  className="rounded text-[14px] font-medium text-black/50 underline decoration-black/20 underline-offset-4 transition hover:text-ink hover:decoration-black/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
                >
                  En savoir plus sur le service
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* À propos / FAQ / Contact — real in-page sections in normal document
          flow (no more modal/backdrop/overlay). Each keeps the distinct
          content treatment already designed; only the modal chrome around
          them was removed. */}

      {/* À propos — calm, text-forward: a lead sentence in a heavier
          weight followed by supporting detail, set off by a single
          brand rule (quote-like), no lists, no icons. */}
      <section id="apropos" className="scroll-mt-20 border-t border-black/5 bg-white px-6 py-20 sm:px-8 sm:py-28">
        <div className="mx-auto max-w-2xl">
          <p className="text-[13px] font-semibold uppercase tracking-wide text-brand-600">À propos</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-ink sm:text-3xl">CTCV-CUOMO, Hôpital Fann</h2>
          <div className="mt-6 border-l-2 border-brand-200 pl-5 sm:mt-8">
            <p className="text-[17px] leading-relaxed text-ink/80 sm:text-[19px]">
              Le service de Chirurgie Thoracique et Cardiovasculaire (CTCV) et le Centre
              Cardio-Pédiatrique Cuomo (CUOMO) du Centre Hospitalier National Fann prennent en
              charge le parcours complet des patients — admission, programme opératoire, suivi
              post-opératoire et sortie.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-black/55">
              Cette plateforme est l'outil interne du personnel soignant et administratif pour
              centraliser les dossiers et coordonner les différents services.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ — a vertical timeline: a single line runs top-to-bottom through
          the section with a dot node per question, items alternating left/
          right of the line on desktop and collapsing to one column (icon
          left, line left) on mobile. Direction-aware via the same
          IntersectionObserver/faqInView plumbing (now toggled both ways,
          not fire-once): the line "draws" downward and each item
          slides/fades in from its side as the section enters the
          viewport, then reverses — sliding/fading back out — as the
          section leaves the viewport, so it replays every time. */}
      <section
        id="faq"
        ref={faqSectionRef}
        className="scroll-mt-20 border-t border-black/5 bg-mist px-6 py-20 sm:px-8 sm:py-28"
      >
        <div className="mx-auto max-w-3xl">
          <p className="text-[13px] font-semibold uppercase tracking-wide text-brand-600">FAQ</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-ink sm:text-3xl">Questions fréquentes</h2>

          <div className="relative mt-10 sm:mt-14">
            {/* faint permanent track + the drawn line on top of it — scales
                0→1 (and back) on the vertical axis as the section
                enters/leaves the viewport */}
            <div aria-hidden="true" className="absolute left-4 top-0 h-full w-px bg-black/10 sm:left-1/2">
              <div
                className={`h-full w-full origin-top bg-brand-600 transition-transform duration-[900ms] ease-[cubic-bezier(0.65,0,0.35,1)] motion-reduce:transition-none ${
                  faqInView ? 'scale-y-100' : 'scale-y-0'
                }`}
              />
            </div>

            <div className="space-y-10 sm:space-y-14">
              {FAQS.map(({ q, a }, i) => {
                const isLeft = i % 2 === 0;
                const delay = `${0.18 + i * 0.22}s`;
                return (
                  <div key={q} className="relative pl-10 sm:grid sm:grid-cols-2 sm:gap-x-10 sm:pl-0">
                    {/* node dot — sits on the line, halo matches the section
                        background so it reads as a punch-through */}
                    <span
                      aria-hidden="true"
                      className={`absolute left-4 top-2 h-3 w-3 -translate-x-1/2 rounded-full bg-brand-600 ring-4 ring-mist transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none sm:left-1/2 ${
                        faqInView ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                      }`}
                      style={{ transitionDelay: delay }}
                    />

                    <div
                      className={`flex items-start gap-3 text-left transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none ${
                        isLeft
                          ? 'sm:col-start-1 sm:flex-row-reverse sm:justify-self-end sm:pr-10 sm:text-right'
                          : 'sm:col-start-2 sm:justify-self-start sm:pl-10'
                      } ${
                        faqInView
                          ? 'translate-x-0 opacity-100'
                          : isLeft
                          ? '-translate-x-6 opacity-0'
                          : 'translate-x-6 opacity-0'
                      }`}
                      style={{ transitionDelay: delay }}
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-brand-600 shadow-sm ring-1 ring-black/5">
                        <FiHelpCircle size={16} aria-hidden="true" />
                      </span>
                      <div>
                        <h3
                          className={`text-[15px] font-semibold text-ink ${
                            isLeft ? 'text-left sm:text-right' : 'text-left'
                          }`}
                        >
                          {q}
                        </h3>
                        <p
                          className={`mt-1.5 text-[14px] leading-relaxed text-black/60 ${
                            isLeft ? 'text-left sm:text-right' : 'text-left'
                          }`}
                        >
                          {a}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Contact — direct and action-oriented: a real, working form, distinct
          from À propos's text layout and FAQ's sweep-reveal list. Submits to
          the production API; the two people to reach stay visible above the
          form as brief, honest context (no fabricated emails/phone numbers). */}
      <section id="contact" className="scroll-mt-20 border-t border-black/5 bg-white px-6 py-20 sm:px-8 sm:py-28">
        <div className="mx-auto max-w-2xl">
          <p className="text-[13px] font-semibold uppercase tracking-wide text-brand-600">Contact</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-ink sm:text-3xl">Une question sur la plateforme ?</h2>

          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 border-b border-black/5 pb-5 text-[13px] text-black/50">
            <span className="inline-flex items-center gap-1.5">
              <FiUser size={13} className="text-brand-600" aria-hidden="true" />
              Administrateur de la plateforme :
            </span>
          </div>

          <ContactForm />
        </div>
      </section>

      {/* Footer — repeats the same À propos/FAQ/Contact triggers + copyright */}
      <footer className="border-t border-black/5 bg-mist px-6 py-10">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-4 text-center">
          <span className="text-[14px] font-semibold tracking-tight text-ink">
            CTCV-CUOMO <span className="font-normal text-black/40">· Hôpital Fann</span>
          </span>
          <div className="flex items-center gap-1">
            {NAV_LINKS.map(({ label, key }) => (
              <a
                key={key}
                href={`#${key}`}
                className="rounded-full px-3 py-1.5 text-[13px] font-medium text-black/55 transition hover:bg-black/5 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 active:bg-black/10"
              >
                {label}
              </a>
            ))}
          </div>
          <p className="text-[12px] text-black/45">
            © {new Date().getFullYear()} Centre Hospitalier National Fann — Chirurgie Thoracique Cardiovasculaire.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
